import { useEffect, useRef, useState } from 'react';
import { BarController, BarElement, CategoryScale, Chart, LinearScale, Tooltip } from 'chart.js';
import type { AffiliateSocialProfile, AffiliateSocials, SocialPlatform } from '../api/affilmaster';
import { useFeather } from '../shell/useFeather';
import { Button } from '../components/arc/button/button';
import { ArcPanel } from '../components/arc/primitives/arc-primitives';
import { AffilmasterDetailSectionHeader } from './AffilmasterDetailFields';
import { WebKindIcon, type WebKind } from './AffilmasterAffiliatesPage';
import './affilmaster-socials.css';
import './affilmaster-propagace.css';

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip);

const nf = new Intl.NumberFormat('cs-CZ');
const MESICE_KRATCE = ['led', 'úno', 'bře', 'dub', 'kvě', 'čvn', 'čvc', 'srp', 'zář', 'říj', 'lis', 'pro'];

export const PLATFORM_LABEL: Record<SocialPlatform, string> = {
  youtube: 'YouTube', tiktok: 'TikTok', instagram: 'Instagram', facebook: 'Facebook', twitch: 'Twitch', kick: 'Kick', telegram: 'Telegram', twitter: 'Twitter', web: 'Web',
};

const PLATFORM_COLOR: Record<SocialPlatform, string> = {
  youtube: '#d0312d', tiktok: '#111111', instagram: '#c13584', facebook: '#1877f2', twitch: '#9146ff', kick: '#53fc18', telegram: '#229ed9', twitter: '#1da1f2', web: '#2f9e5b',
};

export function fmtSocialValue(v: number | string | boolean | null): string {
  if (v == null) return '—';
  if (typeof v === 'boolean') return v ? 'Ano' : 'Ne';
  if (typeof v === 'number') return nf.format(v);
  if (/^\d{4}-\d{2}-\d{2}T/.test(v)) return fmtDateTime(v);
  return v;
}

export function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function channelName(p: AffiliateSocialProfile): string {
  if (p.platform === 'web') return p.url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');
  return p.title || (p.handle ? `@${p.handle}` : p.url);
}

function channelUrlLabel(url: string): string {
  return url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');
}

export function ChannelIcon({ p, size = 22 }: { p: AffiliateSocialProfile; size?: number }) {
  const source = p.platform === 'web' ? p.faviconUrl : p.avatarUrl;
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [source]);

  if (source && !imageFailed) {
    return <img className={p.platform === 'web' ? 'am-prop-favicon' : 'am-prop-avatar'} src={source} alt="" style={{ width: size, height: size }} onError={() => setImageFailed(true)} />;
  }
  return <WebKindIcon kind={p.platform as WebKind} size={size} />;
}

function ChannelStatus({ p, refreshing }: { p: AffiliateSocialProfile; refreshing: boolean }) {
  return (
    <div className="am-socials-card-status">
      {refreshing ? <span className="am-socials-badge is-unconfigured">Načítám…</span>
        : p.lastStatus === 'ok' ? <span className="am-socials-badge is-ok">Načteno</span>
        : p.lastStatus ? <span className="am-socials-badge is-error">Chyba</span>
        : <span className="am-socials-badge is-unsupported">Zatím bez dat</span>}
      <small>{p.snapshotDate ? `Stav k ${p.snapshotDate.split('-').reverse().join('. ')}` : 'data se teprve sbírají'}</small>
    </div>
  );
}

export function AffiliatePropagaceSection({ data, loading, refreshing, error, onRefresh, focusProfileId }: {
  data: AffiliateSocials | null;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  onRefresh: () => void;
  focusProfileId: number | null;
}) {
  const profiles = data?.profiles ?? [];
  const [selectedId, setSelectedId] = useState<number | null>(focusProfileId);
  useFeather([data, loading, refreshing, error, selectedId]);

  useEffect(() => {
    if (focusProfileId != null) setSelectedId(focusProfileId);
  }, [focusProfileId]);

  const selected = profiles.find((p) => p.id === selectedId) ?? profiles[0] ?? null;

  return (
    <section className="am-prop">
      <AffilmasterDetailSectionHeader title="Propagace" description="Dosah a výkon propagačních kanálů" action={<Button type="button" size="sm" onClick={onRefresh} disabled={refreshing || loading}>
        <i data-feather="refresh-cw" style={{ width: 14, height: 14 }} /> {refreshing ? 'Aktualizuji…' : 'Aktualizovat'}
      </Button>} />
      <div className="am-prop-toolbar">
        <span className="am-prop-meta">
          <i data-feather="clock" aria-hidden="true" />
          {profiles.length === 0 && !loading ? 'Partner nemá zadané propagační kanály.' : data?.collectedAt ? `Data k ${fmtDateTime(data.collectedAt)}` : 'Data se teprve sbírají.'}
        </span>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {loading && <div className="prototype-loading" aria-label="Načítám"><span /><span /><span /><span /></div>}

      {!loading && profiles.length > 0 && (
        <div className="am-prop-switch" role="tablist" aria-label="Propagační kanály">
          {profiles.map((p) => (
            <button
              key={p.id}
              type="button"
              role="tab"
              aria-selected={selected?.id === p.id}
              className={`am-prop-chip${selected?.id === p.id ? ' is-active' : ''}${p.lastStatus && p.lastStatus !== 'ok' ? ' is-error' : ''}`}
              onClick={() => setSelectedId(p.id)}
            >
              <span className={`am-socials-platform am-socials-platform-${p.platform}`}><ChannelIcon p={p} size={18} /></span>
              <span className="am-prop-chip-body">
                <strong>{PLATFORM_LABEL[p.platform]}</strong>
                <small>{channelName(p)}</small>
              </span>
              <span className="am-prop-chip-value">
                {refreshing && p.stale ? <em>načítám…</em> : p.primary.value != null ? <><strong>{fmtSocialValue(p.primary.value)}</strong><small>{p.primary.label}</small></> : <em>bez dat</em>}
              </span>
            </button>
          ))}
        </div>
      )}

      {!loading && selected && <ChannelPanel p={selected} refreshing={refreshing && selected.stale} />}
    </section>
  );
}

export function ChannelPanel({ p, refreshing, compact = false }: { p: AffiliateSocialProfile; refreshing: boolean; compact?: boolean }) {
  useFeather([p, refreshing]);
  const hasData = p.metrics.length > 0;
  const entries = Object.entries(p.profile ?? {}).filter(([k, v]) => k !== 'note' && v != null && v !== '' && typeof v !== 'object');
  return (
    <ArcPanel as="article" className={`prototype-panel am-socials-card am-prop-card is-${p.lastStatus ?? 'pending'}${compact ? ' is-compact' : ''}`}>
      {compact ? (
        <header className="am-prop-modal-summary">
          <a className="am-prop-modal-link" href={p.url} target="_blank" rel="noreferrer">
            <span className={`am-socials-platform am-socials-platform-${p.platform}`}><ChannelIcon p={p} size={18} /></span>
            <span className="am-prop-modal-link-copy"><small>Adresa kanálu</small><strong>{channelUrlLabel(p.url)}</strong></span>
            <span className="am-prop-modal-link-action">Otevřít {p.platform === 'web' ? 'web' : 'profil'}</span>
          </a>
          <ChannelStatus p={p} refreshing={refreshing} />
        </header>
      ) : (
        <header className="am-socials-card-head">
          <span className={`am-socials-platform am-socials-platform-${p.platform}`}><ChannelIcon p={p} /></span>
          <div className="am-socials-card-title">
            <strong>{PLATFORM_LABEL[p.platform]} · {channelName(p)}</strong>
            <a href={p.url} target="_blank" rel="noreferrer">{channelUrlLabel(p.url)}<i data-feather="external-link" aria-hidden="true" /></a>
          </div>
          <ChannelStatus p={p} refreshing={refreshing} />
        </header>
      )}

      {p.lastError && <p className="am-socials-error"><i data-feather="alert-triangle" />{p.lastError}</p>}
      {typeof p.profile?.note === 'string' && <p className="am-socials-note"><i data-feather="info" />{p.profile.note}</p>}

      {hasData && (
        <dl className="am-socials-metrics">
          {p.metrics.map((m) => (
            <div key={m.key} className={m.key === p.primary.key ? 'is-primary' : ''}><dt>{m.label}</dt><dd>{fmtSocialValue(m.value)}</dd></div>
          ))}
        </dl>
      )}

      <div className="am-prop-chart-wrap">
        <div className="am-prop-chart-head"><span>{p.primary.label} · posledních 12 měsíců</span></div>
        <MonthlyChart monthly={p.monthly} color={PLATFORM_COLOR[p.platform]} label={p.primary.label} />
      </div>

      {p.keywords.length > 0 && (
        <div className="am-socials-keywords">
          <div className="am-socials-items-head"><span>Pozice na klíčová slova ({p.keywords[0]?.country.toUpperCase()})</span></div>
          <ul>
            {p.keywords.map((k) => (
              <li key={k.keyword}><span>{k.keyword}</span><strong className={k.position == null ? 'is-none' : ''}>{k.position != null ? `${k.position}.` : `mimo top ${k.checkedTop}`}</strong></li>
            ))}
          </ul>
        </div>
      )}

      {p.posts.length > 0 && (
        <div className="am-socials-items">
          <div className="am-socials-items-head"><span>Poslední obsah</span><small>{p.posts.length} položek</small></div>
          <table>
            <thead><tr><th>Titulek</th><th>Datum</th><th className="num">Zhlédnutí</th><th className="num">Lajky</th><th className="num">Komentáře</th></tr></thead>
            <tbody>
              {p.posts.map((it, i) => (
                <tr key={it.externalId ?? i}>
                  <td className="am-socials-item-title">{it.url ? <a href={it.url} target="_blank" rel="noreferrer">{it.title || '(bez titulku)'}</a> : (it.title || '(bez titulku)')}</td>
                  <td data-label="Datum">{fmtDateTime(it.publishedAt)}</td>
                  <td className="num" data-label="Zhlédnutí">{fmtSocialValue(it.views)}</td>
                  <td className="num" data-label="Lajky">{fmtSocialValue(it.likes)}</td>
                  <td className="num" data-label="Komentáře">{fmtSocialValue(it.comments)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {entries.length > 0 && <ProfileDetails entries={entries} />}
    </ArcPanel>
  );
}

function ProfileDetails({ entries }: { entries: [string, unknown][] }) {
  const [open, setOpen] = useState(false);
  useFeather([open]);
  return (
    <div className="am-socials-profile">
      <button type="button" className="am-socials-toggle" onClick={() => setOpen((v) => !v)}>
        <i data-feather={open ? 'chevron-up' : 'chevron-down'} />{open ? 'Skrýt detail profilu' : `Detail profilu (${entries.length} polí)`}
      </button>
      {open && (
        <dl className="am-socials-profile-grid">
          {entries.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{typeof v === 'boolean' ? (v ? 'Ano' : 'Ne') : String(v)}</dd></div>)}
        </dl>
      )}
    </div>
  );
}

function MonthlyChart({ monthly, color, label }: { monthly: { ym: string; value: number | null }[]; color: string; label: string }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const points = monthly.filter((m) => m.value != null).length;

  useEffect(() => {
    if (!canvasRef.current || points === 0) return;
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;
    const labels = monthly.map((m) => {
      const [y, mo] = m.ym.split('-');
      return `${MESICE_KRATCE[Number(mo) - 1] ?? mo} ${String(y).slice(2)}`;
    });
    const chart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels,
        datasets: [{
          label,
          data: monthly.map((m) => m.value),
          backgroundColor: `${color}cc`,
          hoverBackgroundColor: color,
          borderRadius: 4,
          maxBarThickness: 34,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => `${label}: ${c.parsed.y == null ? '—' : nf.format(Number(c.parsed.y))}` } } },
        scales: {
          x: { grid: { display: false }, ticks: { font: { size: 10 } } },
          y: { beginAtZero: true, ticks: { font: { size: 10 }, callback: (v) => nf.format(Number(v)) } },
        },
      },
    });
    return () => chart.destroy();
  }, [monthly, color, label, points]);

  if (points === 0) return <div className="am-prop-chart-empty">Historie se začne kreslit od prvního sběru dat.</div>;
  return <div className="am-prop-chart"><canvas ref={canvasRef} /></div>;
}
